// Sanity image URL builder — pure math on Sanity's asset refs, no API calls
// or secrets, so this is safe to run in the browser.
//
// Reference: https://www.sanity.io/docs/image-url
// Port of the minimal subset of @sanity/image-url.

const PROJECT_ID =
  import.meta.env.VITE_SANITY_PROJECT_ID ?? 'x3097cti';
const DATASET =
  import.meta.env.VITE_SANITY_DATASET ?? 'production';

const CDN_BASE = `https://cdn.sanity.io/images/${PROJECT_ID}/${DATASET}`;

export interface SanityImageAsset {
  _ref?: string;
  _id?: string;
  url?: string;
}

export interface SanityImage {
  asset?: SanityImageAsset | null;
  hotspot?: { x: number; y: number } | null;
  crop?: unknown;
  alt?: string | null;
}

export interface SanityImageOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpg' | 'png' | 'auto';
  fit?: 'crop' | 'clip' | 'fill' | 'max' | 'min' | 'scale';
  blur?: number;
}

function parseAssetRef(ref: string): { filename: string; dims?: string; ext: string } {
  // Format: image-<hash>-<dims>-<ext>  e.g. image-abc123-800x600-jpg
  const [, hash, dims, ext] = ref.split('-');
  return {
    filename: `${hash}-${dims}.${ext}`,
    dims,
    ext,
  };
}

export function urlForSanityImage(
  image: SanityImage | SanityImageAsset | string | null | undefined,
  opts: SanityImageOptions = {}
): string | null {
  if (!image) return null;

  let ref: string | undefined;
  if (typeof image === 'string') {
    ref = image;
  } else if ('asset' in image && image.asset) {
    ref = image.asset._ref ?? image.asset._id ?? image.asset.url;
  } else if ('_ref' in image || '_id' in image) {
    ref = (image as SanityImageAsset)._ref ?? (image as SanityImageAsset)._id;
  }
  if (!ref) return null;

  // If it's already a URL, just return it (possibly with query params)
  if (ref.startsWith('http')) return ref;

  if (!ref.startsWith('image-')) return null;

  const { filename } = parseAssetRef(ref);
  const params = new URLSearchParams();
  if (opts.width) params.set('w', String(opts.width));
  if (opts.height) params.set('h', String(opts.height));
  if (opts.quality) params.set('q', String(opts.quality));
  if (opts.format) params.set('fm', opts.format);
  if (opts.fit) params.set('fit', opts.fit);
  if (opts.blur) params.set('blur', String(opts.blur));
  // Sensible defaults
  if (!opts.quality) params.set('q', '85');
  if (!opts.format) params.set('auto', 'format');

  const qs = params.toString();
  return `${CDN_BASE}/${filename}${qs ? `?${qs}` : ''}`;
}
